/* eslint-disable react-refresh/only-export-components */
import React, { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { AlertTriangle, Download, File, FileX, Loader2, X } from "lucide-react";
import { throttle } from "lodash";
import { MessageType } from "@/model/csr-chat";
import { emitNewMessage } from "@/services/csr-chat/socket-service";
import {
  useUploadAttachment,
  useDownloadAttachment,
} from "@/hooks/use-csr-chat";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import {
  removePendingFile,
  updateMessage,
} from "@/store/csr-chat/csr-chat-slice";
import { cn } from "@/lib/utils";
import he from "he";

export type AttachmentMessageProps = {
  uploadFile?: boolean;
  fileName: string;
  fileType: string;
  fileKey?: string;
  tempMessageId?: string;
  fileSize: number;
  chatChannel: string;
};

export function parseAttachmentProps(
  messageStr: string,
  chatChannel: string,
): AttachmentMessageProps {
  try {
    const parsed = JSON.parse(he.decode(messageStr));
    return {
      uploadFile: parsed.uploadFile || false,
      fileName: parsed.originalName || "",
      fileType: parsed.fileType || "",
      fileKey: parsed.key,
      tempMessageId: parsed.tempMessageId,
      fileSize: parsed.size || 0,
      chatChannel,
    };
  } catch {
    return { fileName: "", fileType: "", fileSize: 0, chatChannel };
  }
}

export const AttachmentMessage = React.memo(
  ({
    uploadFile,
    fileType,
    fileName,
    fileKey,
    tempMessageId,
    fileSize,
    chatChannel,
  }: AttachmentMessageProps) => {
    const dispatch = useDispatch();
    const pendingFile = useSelector((state: RootState) =>
      tempMessageId ? state?.csrChat?.pendingFiles?.[tempMessageId] : undefined,
    );

    const [fileCorrupted, setFileCorrupted] = useState(false);

    const {
      mutate: uploadMutate,
      isPending: uploading,
      isError: uploadFailed,
      reset: resetUpload,
      uploadProgress,
    } = useUploadAttachment(chatChannel, tempMessageId);

    const {
      mutate: downloadMutate,
      isPending: downloading,
      isError: downloadError,
      downloadProgress,
    } = useDownloadAttachment();

    const CORRUPT_NAME = "File Corrupted";

    useEffect(() => {
      setFileCorrupted(
        (!fileType || !fileKey || !fileName || !fileSize) && !uploadFile,
      );
    }, [fileName, fileKey, fileSize, fileType, uploadFile]);

    const sendFileMessage = throttle((messageText: string) => {
      emitNewMessage({
        chatChannel,
        message: messageText.trim(),
        tempId: tempMessageId || "",
        type: MessageType.FILE,
      });
    }, 500);

    useEffect(() => {
      if (uploadFile && pendingFile) {
        uploadMutate(pendingFile, {
          onSuccess: ({ key, originalName, fileType: ft, size }) => {
            if (tempMessageId) {
              dispatch(
                updateMessage({
                  chatChannel,
                  messageId: tempMessageId,
                  content: JSON.stringify({
                    key,
                    originalName,
                    fileType: ft,
                    size,
                    tempMessageId,
                  }),
                }),
              );
            }
            sendFileMessage(
              JSON.stringify({ key, originalName, fileType: ft, size }),
            );
          },
          onSettled: () => {
            if (tempMessageId) dispatch(removePendingFile(tempMessageId));
          },
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatFileSize = (size: number): string => {
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
      return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    };

    const formatFileName = (name: string): string => {
      if (!name) return "";
      return name.length > 18 ? `${name.slice(0, 18)}...` : name;
    };

    const handleDownload = (s3Key: string | undefined) => {
      if (uploadFile || fileCorrupted || downloading || !s3Key) return;
      downloadMutate(s3Key, {
        onSuccess: (blob) => {
          const url = window.URL.createObjectURL(new Blob([blob]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        },
      });
    };

    const displayName = fileCorrupted ? CORRUPT_NAME : formatFileName(fileName);

    return (
      <div>
        <div
          className={cn(
            "flex min-w-[180px] max-w-[220px] items-center gap-2 rounded-lg border bg-white px-3 py-2",
            !uploadFile && !fileCorrupted && "hover:bg-gray-50",
            fileCorrupted && "opacity-60",
          )}
        >
          <div className="flex-shrink-0 text-gray-500">
            {uploadFile ? (
              uploadFailed ? (
                <X className="size-5 text-red-500" />
              ) : uploading && uploadProgress < 100 ? (
                <ProgressSpinner text={`${uploadProgress}%`} />
              ) : (
                <ProgressSpinner text="Scanning" />
              )
            ) : fileCorrupted ? (
              <FileX className="size-5 text-red-400" />
            ) : (
              <File className="size-5" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p
              className="truncate text-sm font-medium"
              title={fileCorrupted ? CORRUPT_NAME : fileName}
            >
              {displayName}
            </p>
            <p className="text-xs text-gray-400">
              {fileCorrupted ? "0 B" : formatFileSize(fileSize)}
            </p>
          </div>
          {!uploadFile && !fileCorrupted && (
            // 2.1.1 Keyboard — A / 4.1.2 — A: the download control was a <div onClick>,
            // so an attachment sent by member support could not be downloaded without a
            // mouse, and it was announced as plain text.
            <button
              type="button"
              aria-label="Download attachment"
              className="flex-shrink-0 cursor-pointer rounded text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(fileKey);
              }}
            >
              {downloading ? (
                <ProgressSpinner text={`${downloadProgress}%`} />
              ) : (
                <Download aria-hidden="true" className="size-4" />
              )}
            </button>
          )}
        </div>

        {downloadError && (
          <p className="mt-1 flex items-center justify-end gap-1 text-right text-xs text-red-600">
            <AlertTriangle className="size-3" />
            Download failed, please retry!
          </p>
        )}

        <Dialog.Root
          open={uploadFailed}
          onOpenChange={(open) => {
            if (!open) resetUpload();
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
            <Dialog.Content
              aria-describedby={undefined}
              className="fixed left-1/2 top-1/2 z-50 w-80 -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-6 shadow-xl"
            >
              <Dialog.Title className="mb-4 text-center text-base font-semibold">
                File Upload Failed
              </Dialog.Title>
              <div className="flex justify-center">
                <button
                  className="rounded-lg bg-primary px-6 py-2 text-sm text-white hover:opacity-90"
                  onClick={resetUpload}
                >
                  Ok
                </button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    );
  },
);

AttachmentMessage.displayName = "AttachmentMessage";

const ProgressSpinner = ({ text }: { text: string }) => (
  <div className="flex items-center gap-1">
    <Loader2 className="size-3 animate-spin" />
    <span className="text-xs">{text}</span>
  </div>
);
